import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { employeeService } from "services";
import {
  EmployeeForm,
  EmployeeQuery,
  EmployeeUpdatePayload,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";

type EmployeeListKey = ["employees"] | ["employees", EmployeeQuery];
type EmployeeDetailKey = ["employees", "detail", number | string];

const employeeKeys = {
  all: ["employees"] as const,
  list: (params?: EmployeeQuery): EmployeeListKey =>
    params ? ["employees", params] : ["employees"],
  detail: (id: number | string): EmployeeDetailKey => [
    "employees",
    "detail",
    id,
  ],
};

export function useEmployee() {
  const queryClient = useQueryClient();

  const fetchList = (params?: EmployeeQuery, enabled = true) => {
    return useQuery({
      queryKey: employeeKeys.list(params),
      queryFn: () => employeeService.list(params),
      enabled,
      select: (data) => ({
        data: data?.data ?? [],
        totalPage: data?.totalPage ?? 1,
      }),
    });
  };

  const fetchDetail = (id: number | string, enabled = true) => {
    return useQuery({
      queryKey: employeeKeys.detail(id),
      queryFn: () => employeeService.show(id),
      enabled: Boolean(id) && enabled,
    });
  };

  const createEmployee = useMutation({
    mutationFn: (payload: EmployeeForm) => employeeService.store(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200 || res?.statusCode === 201) {
        SnackbarUtils.success(res?.message ?? "Tạo nhân viên thành công");
      }
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Tạo nhân viên thất bại");
      console.error("createEmployee failed", err);
    },
  });

  const updateEmployee = useMutation({
    mutationFn: (payload: EmployeeUpdatePayload) =>
      employeeService.update(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(res?.message ?? "Cập nhật nhân viên thành công");
      }
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Cập nhật nhân viên thất bại");
      console.error("updateEmployee failed", err);
    },
  });

  const deleteEmployee = useMutation({
    mutationFn: (id: number | string) => employeeService.destroy(id),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(res?.message ?? "Xoá nhân viên thành công");
      }
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Xoá nhân viên thất bại");
      console.error("deleteEmployee failed", err);
    },
  });

  return {
    fetchList,
    fetchDetail,
    createEmployee,
    updateEmployee,
    deleteEmployee,
  };
}
